import {useQueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useState} from "react";

import {ComplianceReconcileOverlay} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {useBlockingGate} from "@/src/modules/core/hooks/use-blocking-gate";
import {useComplianceGateCoordinates} from "@/src/modules/core/hooks/use-compliance-gate-coordinates";
import {resolveShiftGpsForSubmit} from "@/src/modules/core/lib/resolve-shift-gps-coordinates";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {reconcileCompliance} from "@/src/modules/core/lib/reconcile-compliance";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useSetBlockingGateReconciling} from "@/src/modules/core/store/blocking-gate";
import {useShiftsStore, useStoredActiveShift} from "@/src/modules/shifts/store";
import {Shift, ShiftBreak} from "@/src/modules/shifts/types";

import {useBreakAction} from "../../hooks";
import {
  getBreakActionErrorTitle,
  isBreakBusinessError,
} from "../../lib/break-action-errors";
import {findActiveShiftBreak, getShiftBreaks} from "@/src/modules/shifts/lib/shift-breaks";
import {BreakAction, BreakActionRequest} from "../../types";
import {BreakActiveModal} from "./break-active-modal";

const MESSAGES = {
  [BreakAction.START]: "Your break has started",
  [BreakAction.COMPLETE]: "Your break has been completed",
  [BreakAction.DELAY]: "Your break has been delayed",
  [BreakAction.INTERRUPT]: "Your break has been interrupted",
  [BreakAction.REJECT]: "Your break has been rejected",
};

function isBreakStillActiveOnShift(shift: Shift, shiftBreakId: number) {
  return getShiftBreaks(shift).some(
    (b) => b.shift_break_id === shiftBreakId && b.status === "active",
  );
}

export function BreakActiveAlert() {
  const toast = useToast();
  const me = useStoredMe();
  const viewingAsUser = isViewingAsUser(me);
  const queryClient = useQueryClient();
  const activeShift = useStoredActiveShift();
  const {shiftBreak, setShiftBreak} = useActiveBreak();
  const [actionError, setActionError] = useState<{
    message: string;
    action: BreakAction;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const {isPending, mutateAsync: breakAction} = useBreakAction();
  const setReconciling = useSetBlockingGateReconciling();

  const present = !viewingAsUser && shiftBreak != null;
  const {isVisible, showReconcilingOverlay} = useBlockingGate({id: "break-active", present});
  const {submitBlockedByLocation} = useComplianceGateCoordinates(present);

  useEffect(() => {
    function handleBreakAutocompleted(event: Event) {
      const breakData = (event as CustomEvent<ShiftBreak>).detail;
      const completedId = breakData?.shift_break_id;

      void (async () => {
        setReconciling(true, "break-active");
        try {
          await reconcileCompliance(queryClient, {shiftBreakId: completedId});
          if (completedId != null && shiftBreak?.shift_break_id === completedId) {
            setShiftBreak(null);
          }
        } finally {
          setReconciling(false, "break-active");
        }
      })();
    }

    window.addEventListener("break:autocompleted", handleBreakAutocompleted);

    return () => {
      window.removeEventListener("break:autocompleted", handleBreakAutocompleted);
    };
  }, [queryClient, setReconciling, shiftBreak?.shift_break_id]);

  useEffect(() => {
    if (!activeShift || viewingAsUser) {
      setShiftBreak(null);
      return;
    }

    const availableBreak = findActiveShiftBreak(activeShift);

    if (availableBreak) {
      setShiftBreak((prev) =>
        prev?.shift_break_id === availableBreak.shift_break_id ? prev : availableBreak,
      );
    } else {
      setShiftBreak((prev) => (prev === null ? prev : null));
    }
  }, [activeShift, viewingAsUser]);

  useEffect(() => {
    setActionError(null);
  }, [shiftBreak?.shift_break_id]);

  const handleBreakTimeElapsed = useCallback(async () => {
    if (!shiftBreak?.shift_break_id) {
      return;
    }
    setReconciling(true, "break-active");
    try {
      await reconcileCompliance(queryClient, {shiftBreakId: shiftBreak.shift_break_id});
    } finally {
      setReconciling(false, "break-active");
    }
  }, [queryClient, setReconciling, shiftBreak?.shift_break_id]);

  if (!shiftBreak || viewingAsUser) return null;

  const handleBreakAction = async (
    action: BreakAction,
    {reason, delay_minutes}: {reason?: string; delay_minutes?: number} = {},
  ) => {
    setActionError(null);
    setActionBusy(true);
    setGpsBusy(true);
    try {
      const gps = await resolveShiftGpsForSubmit(activeShift, "break_action");
      setGpsBusy(false);
      if (!gps) {
        return;
      }
      const data = {
        shift_break_id: shiftBreak.shift_break_id,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        action,
      } as BreakActionRequest;

      if (reason) {
        data.reason = reason;
      }

      if (delay_minutes) {
        data.delay_minutes = delay_minutes;
      }

      await breakAction(data);
      toast.success(MESSAGES[action as keyof typeof MESSAGES]);

      setReconciling(true, "break-active");
      try {
        await reconcileCompliance(
          queryClient,
          action === BreakAction.DELAY
            ? undefined
            : {shiftBreakId: shiftBreak.shift_break_id},
        );
      } finally {
        setReconciling(false, "break-active");
      }
      setShiftBreak(null);
    } catch (e) {
      if (!isBreakBusinessError(e)) return;

      setActionError({message: getBreakActionErrorTitle(e), action});

      const message = (e as {response?: {data?: {message?: string}}})?.response?.data?.message
        ?.toLowerCase() ?? "";
      const isNoActiveBreak = message.includes("no active break");

      if (action === BreakAction.COMPLETE && isNoActiveBreak) {
        setReconciling(true, "break-active");
        try {
          await reconcileCompliance(queryClient, {shiftBreakId: shiftBreak.shift_break_id});
        } finally {
          setReconciling(false, "break-active");
        }
        const refreshedShift = useShiftsStore.getState().activeShift;
        if (
          !refreshedShift ||
          !isBreakStillActiveOnShift(refreshedShift, shiftBreak.shift_break_id)
        ) {
          setShiftBreak(null);
        }
      }
    } finally {
      setGpsBusy(false);
      setActionBusy(false);
    }
  };

  return (
    <BreakActiveModal
      open={isVisible}
      showReconcilingOverlay={showReconcilingOverlay}
      isPending={isPending}
      actionBusy={actionBusy}
      gpsBusy={gpsBusy}
      submitBlockedByLocation={submitBlockedByLocation}
      shiftBreak={shiftBreak}
      actionError={actionError}
      onClearActionError={() => setActionError(null)}
      onBreakTimeElapsed={() => {
        void handleBreakTimeElapsed();
      }}
      onComplete={() => handleBreakAction(BreakAction.COMPLETE)}
      onInterrupt={(reason) => handleBreakAction(BreakAction.INTERRUPT, {reason})}
    />
  );
}

function useActiveBreak() {
  const [shiftBreak, setShiftBreak] = useState<ShiftBreak | null>(null);
  return {shiftBreak, setShiftBreak};
}
